import { useEffect, useState, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  Pencil,
  ImageIcon,
  CheckCircle2,
  Clapperboard,
  Play,
  ArrowUp,
  ArrowDown,
  Copy,
  Trash2,
  Loader2,
  AlertTriangle,
} from "lucide-react";
import { Button } from "@/components/primitives/Button";
import { Input } from "@/components/primitives/Input";
import {
  type Scene,
  type Reference,
  type Keyframe,
  type Clip,
  listKeyframes,
  listClips,
  generateKeyframe,
  animateScene,
  updateScene,
  duplicateScene,
  moveScene,
  deleteScene,
} from "@/lib/api";
import { API } from "@/config";
import { translateError } from "@/lib/errors";

const clipSrc = (u: string, v?: string) =>
  `${API}${u.replace(/^\/api/, "")}${v ? `?v=${v}` : ""}`;

const inProgressClip = (m: Media) =>
  !!m.clip && (m.clip.status === "PENDING" || m.clip.status === "GENERATING");

const KEYFRAME_COST = "0,15";
const VIDEO_COST = "0,42";

type Media = { kf?: Keyframe; clip?: Clip };

function statusFor(m: Media): { labelKey: string; cls: string } {
  const c = m.clip;
  if (c) {
    if (c.status === "READY") return { labelKey: "sceneBoard.statusVideoReady", cls: "bg-[#3DDC84]/15 text-[#3DDC84] border-[#3DDC84]/40" };
    if (c.status === "STALLED") return { labelKey: "sceneBoard.statusAttention", cls: "bg-[#FFB020]/15 text-[#FFB020] border-[#FFB020]/40" };
    if (c.status === "FAILED") return { labelKey: "sceneBoard.statusFailed", cls: "bg-[#FF5C6C]/15 text-[#FF5C6C] border-[#FF5C6C]/40" };
    return { labelKey: "sceneBoard.statusGenerating", cls: "bg-[#2E7BFF]/15 text-[#2E7BFF] border-[#2E7BFF]/40" };
  }
  if (m.kf?.image_url) return { labelKey: "sceneBoard.statusKeyframeReady", cls: "bg-[#2E7BFF]/15 text-[#2E7BFF] border-[#2E7BFF]/40" };
  return { labelKey: "sceneBoard.statusToPrepare", cls: "bg-white/5 text-muted-foreground border-border" };
}

export function SceneBoard({
  scenes,
  references,
  onChanged,
}: {
  scenes: Scene[];
  references: Reference[];
  onChanged: () => void;
}) {
  const { t } = useTranslation();
  const [media, setMedia] = useState<Record<string, Media>>({});
  const [editing, setEditing] = useState<Scene | null>(null);
  const [editForm, setEditForm] = useState({ title: "", description: "" });
  const [preview, setPreview] = useState<{ scene: Scene; m: Media } | null>(null);
  const [confirm, setConfirm] = useState<{ title: string; cost: string; note?: string; run: () => void } | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const prevStatus = useRef<Record<string, string>>({});

  const loadMedia = useCallback(async () => {
    const entries = await Promise.all(
      scenes.map(async (s) => {
        const [kfs, clips] = await Promise.all([
          listKeyframes(s.id).catch(() => [] as Keyframe[]),
          listClips(s.id).catch(() => [] as Clip[]),
        ]);
        const kf = [...kfs].reverse().find((k) => k.image_url) || kfs[kfs.length - 1];
        const clip = [...clips].reverse().find((c) => c.status === "READY") || clips[clips.length - 1];
        return [s.id, { kf, clip }] as const;
      }),
    );
    setMedia(Object.fromEntries(entries));
  }, [scenes]);

  useEffect(() => {
    loadMedia();
  }, [loadMedia]);

  // Auto-poll while any clip is still generating (recovers state on refresh; no re-submit).
  useEffect(() => {
    // Detect completion transitions to surface a clear message once.
    for (const [sid, m] of Object.entries(media)) {
      const cur = m.clip?.status;
      const prev = prevStatus.current[sid];
      if (cur && prev && prev !== cur) {
        if (cur === "READY") toast.success(t("sceneBoard.videoReady"));
        else if (cur === "FAILED") toast.error(t("sceneBoard.videoFailed"));
        else if (cur === "STALLED") toast.warning(t("sceneBoard.videoAttention"));
      }
      if (cur) prevStatus.current[sid] = cur;
    }
    const anyInProgress = Object.values(media).some(inProgressClip);
    if (!anyInProgress) return;
    const timer = setTimeout(() => loadMedia(), 5000);
    return () => clearTimeout(timer);
  }, [media, loadMedia, t]);

  const doGenerateKeyframe = (s: Scene) =>
    setConfirm({
      title: t("sceneBoard.genKeyframeTitle", { title: s.title }),
      cost: KEYFRAME_COST,
      note:
        (s.reference_ids || []).length === 0
          ? t("sceneBoard.noRefNote")
          : undefined,
      run: async () => {
        setBusy(s.id);
        try {
          const kf = await generateKeyframe(s.id);
          if (kf?.warning) toast.warning(kf.warning, { duration: 8000 });
          // Reset approval so the freshly generated keyframe must be reviewed & re-approved
          // before a new video can be generated. Existing keyframe/video stay intact.
          if (s.keyframe_approved) {
            await updateScene(s.id, { keyframe_approved: false } as any);
          }
          toast.success(t("sceneBoard.keyframeGenerated"));
          await loadMedia();
          onChanged();
        } catch (e: any) {
          toast.error(translateError(e?.response?.data?.detail, "sceneBoard.keyframeFailed"));
        } finally {
          setBusy(null);
        }
      },
    });

  const doApprove = async (s: Scene) => {
    try {
      await updateScene(s.id, { keyframe_approved: true } as any);
      toast.success(t("sceneBoard.keyframeApproved"));
      onChanged();
    } catch {
      toast.error(t("sceneBoard.approveFailed"));
    }
  };

  const toggleRef = async (s: Scene, refId: string) => {
    const cur = s.reference_ids || [];
    const next = cur.includes(refId) ? cur.filter((r) => r !== refId) : [...cur, refId];
    try {
      await updateScene(s.id, { reference_ids: next } as any);
      onChanged();
    } catch {
      toast.error(t("sceneBoard.refsUpdateFailed"));
    }
  };

  const doGenerateVideo = (s: Scene) =>
    setConfirm({
      title: t("sceneBoard.genVideoTitle", { title: s.title }),
      cost: VIDEO_COST,
      run: async () => {
        setBusy(s.id);
        try {
          const clip = await animateScene(s.id);
          if (clip?.status === "FAILED") {
            toast.error(t("sceneBoard.videoFailed"));
          } else {
            toast.success(t("sceneBoard.videoStarted"));
          }
          await loadMedia();
          onChanged();
        } catch (e: any) {
          toast.error(translateError(e?.response?.data?.detail, "sceneBoard.videoGenFailed"));
        } finally {
          setBusy(null);
        }
      },
    });

  const doMove = async (s: Scene, dir: "up" | "down") => {
    try {
      await moveScene(s.id, dir);
      onChanged();
    } catch {
      toast.error(t("sceneBoard.reorderFailed"));
    }
  };

  const doDuplicate = async (s: Scene) => {
    try {
      await duplicateScene(s.id);
      toast.success(t("sceneBoard.duplicated"));
      onChanged();
    } catch {
      toast.error(t("sceneBoard.duplicateFailed"));
    }
  };

  const doDelete = async (s: Scene) => {
    if (!window.confirm(t("sceneBoard.deleteConfirm", { title: s.title }))) return;
    try {
      await deleteScene(s.id);
      toast.success(t("sceneBoard.deleted"));
      onChanged();
    } catch {
      toast.error(t("sceneBoard.deleteFailed"));
    }
  };

  const openEdit = (s: Scene) => {
    setEditForm({ title: s.title, description: s.description || "" });
    setEditing(s);
  };
  const saveEdit = async () => {
    if (!editing) return;
    try {
      await updateScene(editing.id, { title: editForm.title, description: editForm.description });
      toast.success(t("sceneBoard.updated"));
      setEditing(null);
      onChanged();
    } catch {
      toast.error(t("sceneBoard.saveFailed"));
    }
  };

  return (
    <div data-testid="scene-board" className="grid gap-5 sm:grid-cols-2 xl:grid-cols-3">
      {scenes.map((s, idx) => {
        const m = media[s.id] || {};
        const st = statusFor(m);
        const hasKf = !!m.kf?.image_url;
        const hasVideo = m.clip?.status === "READY" && !!m.clip.video_url;
        const generating = inProgressClip(m);
        // A regenerated keyframe resets approval: show it for review even if an old video exists.
        const pendingKf = hasKf && !s.keyframe_approved;
        const showVideo = hasVideo && !pendingKf;
        const stale =
          hasKf &&
          !!m.kf?.created_at &&
          !!s.content_updated_at &&
          new Date(s.content_updated_at).getTime() > new Date(m.kf!.created_at!).getTime();
        return (
          <div
            key={s.id}
            data-testid="scene-board-card"
            className="flex flex-col overflow-hidden rounded-2xl glass-panel"
          >
            {/* Media */}
            <div className="relative aspect-[9/16] max-h-72 w-full overflow-hidden bg-black/40">
              {showVideo ? (
                <video
                  src={clipSrc(m.clip!.video_url!, m.clip!.id)}
                  className="h-full w-full object-cover"
                  muted
                  loop
                  playsInline
                  onMouseOver={(e) => (e.currentTarget as HTMLVideoElement).play()}
                  onMouseOut={(e) => (e.currentTarget as HTMLVideoElement).pause()}
                />
              ) : hasKf ? (
                <img src={m.kf!.image_url!} alt={s.title} className="h-full w-full object-cover" />
              ) : (
                <div className="flex h-full w-full items-center justify-center text-muted-foreground">
                  <ImageIcon className="h-8 w-8 opacity-40" />
                </div>
              )}
              <span className="absolute left-2 top-2 rounded-md bg-black/60 px-2 py-0.5 font-display text-xs font-bold text-[#2E7BFF]">
                {String(idx + 1).padStart(2, "0")}
              </span>
              <span className={`absolute right-2 top-2 rounded-full border px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${st.cls}`}>
                {t(st.labelKey)}
              </span>
              {hasVideo && m.clip?.duration_seconds ? (
                <span className="absolute bottom-2 right-2 rounded bg-black/70 px-1.5 py-0.5 text-[10px] text-white">
                  {Math.round(m.clip.duration_seconds)}s
                </span>
              ) : null}
            </div>

            {/* Body */}
            <div className="flex flex-1 flex-col gap-3 p-4">
              <div>
                <h3 className="font-display text-sm font-semibold uppercase tracking-wide text-foreground">
                  {s.title}
                </h3>
                {s.description ? (
                  <p className="mt-1 line-clamp-2 text-xs text-muted-foreground">{s.description}</p>
                ) : null}
              </div>

              {references.length > 0 && (
                <div className="space-y-1.5">
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                    {t("sceneBoard.refsUsed")}
                  </p>
                  <div className="flex flex-wrap gap-1.5" data-testid="scene-ref-toggles">
                    {references.map((r) => {
                      const on = (s.reference_ids || []).includes(r.id);
                      return (
                        <button
                          key={r.id}
                          onClick={() => toggleRef(s, r.id)}
                          data-testid={`scene-ref-toggle-${r.id}`}
                          className={`rounded-full border px-2.5 py-0.5 text-[10px] transition-colors ${
                            on
                              ? "border-[#2E7BFF]/50 bg-[#2E7BFF]/15 text-[#2E7BFF]"
                              : "border-border bg-transparent text-muted-foreground hover:text-foreground"
                          }`}
                        >
                          {on ? "✓ " : "+ "}
                          {r.name}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {stale && (
                <p className="flex items-start gap-1.5 rounded-md border border-[#FFB020]/30 bg-[#FFB020]/5 px-2 py-1.5 text-[10px] text-[#FFB020]" data-testid="scene-stale-warning">
                  <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />
                  {t("sceneBoard.stale")}
                </p>
              )}

              {/* Actions */}
              <div className="mt-auto flex flex-wrap gap-1.5 pt-1">
                <Button variant="ghost" size="sm" onClick={() => openEdit(s)} data-testid="scene-edit-btn">
                  <Pencil className="h-3.5 w-3.5" /> {t("common.edit")}
                </Button>
                {!generating && (
                  <Button variant="secondary" size="sm" disabled={busy === s.id} onClick={() => doGenerateKeyframe(s)} data-testid="scene-genkf-btn">
                    <ImageIcon className="h-3.5 w-3.5" /> {hasKf ? t("sceneBoard.regenKeyframe") : t("sceneBoard.genKeyframe")}
                  </Button>
                )}
                {hasKf && !s.keyframe_approved && !generating && (
                  <Button variant="ghost" size="sm" onClick={() => doApprove(s)} data-testid="scene-approve-btn">
                    <CheckCircle2 className="h-3.5 w-3.5" /> {t("sceneBoard.approveKeyframe")}
                  </Button>
                )}
                {hasKf && s.keyframe_approved && !generating && (
                  <Button variant="secondary" size="sm" disabled={busy === s.id} onClick={() => doGenerateVideo(s)} data-testid="scene-genvideo-btn">
                    <Clapperboard className="h-3.5 w-3.5" /> {hasVideo ? t("sceneBoard.regenVideo") : t("sceneBoard.genVideo")}
                  </Button>
                )}
                {generating && (
                  <Button variant="secondary" size="sm" disabled data-testid="scene-video-generating">
                    <Loader2 className="h-3.5 w-3.5 animate-spin" /> {t("sceneBoard.videoGenerating")}
                  </Button>
                )}
                {(hasVideo || hasKf) && (
                  <Button variant="ghost" size="sm" onClick={() => setPreview({ scene: s, m })} data-testid="scene-preview-btn">
                    <Play className="h-3.5 w-3.5" /> {t("common.preview")}
                  </Button>
                )}
              </div>

              {pendingKf && hasVideo && (
                <p className="mt-1.5 flex items-start gap-1.5 rounded-md border border-[#2E7BFF]/30 bg-[#2E7BFF]/5 px-2 py-1.5 text-[10px] text-[#2E7BFF]" data-testid="scene-newkf-hint">
                  <CheckCircle2 className="mt-0.5 h-3 w-3 shrink-0" />
                  {t("sceneBoard.newKfHint")}
                </p>
              )}
              {hasVideo && (
                <p className="mt-1.5 text-[10px] text-muted-foreground" data-testid="scene-assets-note">
                  {t("sceneBoard.assetsNote")}
                </p>
              )}

              {/* Utility row */}
              <div className="flex items-center gap-1 border-t border-border pt-2 text-muted-foreground">
                <button title={t("scenes.moveUp")} onClick={() => doMove(s, "up")} className="rounded p-1 hover:text-[#2E7BFF]" data-testid="scene-up-btn">
                  <ArrowUp className="h-3.5 w-3.5" />
                </button>
                <button title={t("scenes.moveDown")} onClick={() => doMove(s, "down")} className="rounded p-1 hover:text-[#2E7BFF]" data-testid="scene-down-btn">
                  <ArrowDown className="h-3.5 w-3.5" />
                </button>
                <button title={t("scenes.duplicate")} onClick={() => doDuplicate(s)} className="rounded p-1 hover:text-[#2E7BFF]" data-testid="scene-duplicate-btn">
                  <Copy className="h-3.5 w-3.5" />
                </button>
                <button title={t("common.delete")} onClick={() => doDelete(s)} className="ml-auto rounded p-1 hover:text-[#FF5C6C]" data-testid="scene-delete-btn">
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          </div>
        );
      })}

      {/* Cost confirmation */}
      {confirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={() => setConfirm(null)}>
          <div className="w-full max-w-md rounded-2xl glass-panel p-6" onClick={(e) => e.stopPropagation()} data-testid="cost-confirm-dialog">
            <h3 className="font-display text-base font-semibold text-foreground">{confirm.title}</h3>
            <p className="mt-2 text-sm text-muted-foreground">
              {t("common.predictedCost")}: <strong className="text-[#2E7BFF]">${confirm.cost}</strong>. {t("common.startsAfterConfirm")}
            </p>
            {confirm.note && (
              <p className="mt-3 flex items-start gap-2 rounded-lg border border-[#FFB020]/30 bg-[#FFB020]/5 px-3 py-2 text-xs text-[#FFB020]" data-testid="keyframe-noref-warning">
                <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                <span>{confirm.note}</span>
              </p>
            )}
            <div className="mt-5 flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setConfirm(null)}>{t("common.cancel")}</Button>
              <Button
                variant="secondary"
                data-testid="cost-confirm-accept"
                onClick={() => {
                  const run = confirm.run;
                  setConfirm(null);
                  run();
                }}
              >
                {t("sceneBoard.generateWithCost", { cost: confirm.cost })}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Edit */}
      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={() => setEditing(null)}>
          <div className="w-full max-w-lg rounded-2xl glass-panel p-6" onClick={(e) => e.stopPropagation()} data-testid="scene-edit-dialog">
            <h3 className="mb-4 font-display text-base font-semibold uppercase tracking-wide text-foreground">{t("sceneBoard.editScene")}</h3>
            <div className="space-y-3">
              <Input
                value={editForm.title}
                onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                placeholder={t("sceneBoard.titlePlaceholder")}
              />
              <textarea
                value={editForm.description}
                onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                placeholder={t("sceneBoard.descriptionPlaceholder")}
                rows={4}
                className="w-full rounded-xl border border-[#2E7BFF]/20 bg-[#05070D] p-3 text-sm text-foreground outline-none focus:border-[#2E7BFF]/60"
              />
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setEditing(null)}>{t("common.cancel")}</Button>
              <Button variant="secondary" onClick={saveEdit} data-testid="scene-edit-save">{t("common.save")}</Button>
            </div>
          </div>
        </div>
      )}

      {/* Preview */}
      {preview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4" onClick={() => setPreview(null)} data-testid="scene-preview-dialog">
          <div className="flex max-h-[85vh] flex-col items-center" onClick={(e) => e.stopPropagation()}>
            <p className="mb-3 font-display text-sm font-semibold uppercase tracking-wide text-[#2E7BFF]">{preview.scene.title}</p>
            {preview.m.clip?.status === "READY" && preview.m.clip.video_url ? (
              <video src={clipSrc(preview.m.clip.video_url, preview.m.clip.id)} controls autoPlay className="max-h-[75vh] rounded-lg" />
            ) : preview.m.kf?.image_url ? (
              <img src={preview.m.kf.image_url} alt="" className="max-h-[75vh] rounded-lg" />
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
}
